<template>
  <HrPageLayout
    title="Edit Employee"
    subtitle="Update employee information"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Employees', route: { name: 'employee-register' } },
      { label: 'Edit Employee' }
    ]"
  >
    <div class="employee-edit-content">
      <!-- Progress Indicator -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body py-3">
          <div class="progress-nav">
            <div class="progress-steps">
              <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                <div class="step-number">1</div>
                <div class="step-title">Personal Info</div>
              </div>
              <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
                <div class="step-number">2</div>
                <div class="step-title">Government IDs</div>
              </div>
              <div class="step" :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
                <div class="step-number">3</div>
                <div class="step-title">Documents</div>
              </div>
              <div class="step" :class="{ active: currentStep >= 4, completed: currentStep > 4 }">
                <div class="step-number">4</div>
                <div class="step-title">Emergency Contacts</div>
              </div>
              <div class="step" :class="{ active: currentStep >= 5, completed: currentStep > 5 }">
                <div class="step-number">5</div>
                <div class="step-title">Next of Kin</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Form -->
      <div class="employee-edit-form-wrap">
        <!-- Step 1: Personal Information -->
        <div v-show="currentStep === 1" class="hr-chart-card">
          <div class="hr-chart-header hr-chart-header-primary">
            <h5 class="hr-chart-title hr-mb-0">
              <i class="bx bx-user-circle"></i>
              Personal Information
            </h5>
          </div>
          <div class="hr-chart-body">
            <div class="row">
              <!-- Employee Number -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Employee Number <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-id-card"></i></span>
                  <input
                      v-model="employee.empNo"
                      type="text"
                      class="hr-input"
                      :class="{'is-invalid': errors.empNo}"
                      placeholder="e.g., EMP001"
                      @blur="validateField('empNo')"
                  >
                </div>
                <small v-if="errors.empNo" class="hr-text-danger">{{ errors.empNo }}</small>
              </div>

              <!-- First Name -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  First Name <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-user"></i></span>
                  <input
                      v-model="employee.firstName"
                      type="text"
                      class="hr-input"
                      :class="{'is-invalid': errors.firstName}"
                      placeholder="Enter first name"
                      @blur="validateField('firstName')"
                  >
                </div>
                <small v-if="errors.firstName" class="hr-text-danger">{{ errors.firstName }}</small>
              </div>

              <!-- Middle Name -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Middle Name
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-user"></i></span>
                  <input
                      v-model="employee.middleName"
                      type="text"
                      class="hr-input"
                      :class="{'is-invalid': errors.middleName}"
                      placeholder="Enter middle name"
                      @blur="validateField('middleName')"
                  >
                </div>
                <small v-if="errors.middleName" class="hr-text-danger">{{ errors.middleName }}</small>
              </div>

              <!-- Last Name -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Last Name <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-user"></i></span>
                  <input
                      v-model="employee.lastName"
                      type="text"
                      class="hr-input"
                      :class="{'is-invalid': errors.lastName}"
                      placeholder="Enter last name"
                      @blur="validateField('lastName')"
                  >
                </div>
                <small v-if="errors.lastName" class="hr-text-danger">{{ errors.lastName }}</small>
              </div>

              <!-- Email -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Work Email <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-envelope"></i></span>
                  <input
                      v-model="employee.email"
                      type="email"
                      class="hr-input"
                      :class="{'is-invalid': errors.email}"
                      placeholder="email@company.com"
                      @blur="validateField('email')"
                  >
                </div>
                <small v-if="errors.email" class="hr-text-danger">{{ errors.email }}</small>
              </div>

              <!-- Personal Email (mandatory for Contract/Consultant/Intern) -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Personal Email
                  <span v-if="requiresPersonalEmail" class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-envelope"></i></span>
                  <input
                      v-model="employee.personalEmail"
                      type="email"
                      class="hr-input"
                      :class="{'is-invalid': errors.personalEmail}"
                      placeholder="personal@gmail.com"
                      @blur="validateField('personalEmail')"
                  >
                </div>
                <small v-if="errors.personalEmail" class="hr-text-danger">{{ errors.personalEmail }}</small>
              </div>

              <!-- Phone -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Phone Number <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-phone"></i></span>
                  <input
                      v-model="employee.phone"
                      type="tel"
                      class="hr-input"
                      :class="{'is-invalid': errors.phone}"
                      placeholder="07XXXXXXXX"
                      @blur="validateField('phone')"
                  >
                </div>
                <small v-if="errors.phone" class="hr-text-danger">{{ errors.phone }}</small>
              </div>

              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">Mobile number</label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-mobile"></i></span>
                  <input v-model="employee.mobileNumber" type="tel" class="hr-input" placeholder="07XXXXXXXX">
                </div>
              </div>

              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">KRA PIN</label>
                <input v-model="employee.kraPin" type="text" class="hr-input" placeholder="e.g. A001234567X">
              </div>

              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">NSSF number</label>
                <input v-model="employee.nssfNumber" type="text" class="hr-input">
              </div>

              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">SHA / SHIF number</label>
                <input v-model="employee.shifNumber" type="text" class="hr-input">
              </div>

              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">Designation code</label>
                <input v-model="employee.designationCode" type="text" class="hr-input">
              </div>

              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">Pay group</label>
                <input v-model="employee.payGroup" type="text" class="hr-input">
              </div>

              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">DOCA (date of current appointment)</label>
                <input v-model="employee.doca" type="date" class="hr-input">
              </div>

              <!-- National ID -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  National ID <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-id-card"></i></span>
                  <input
                      v-model="employee.nationalId"
                      type="text"
                      class="hr-input"
                      :class="{'is-invalid': errors.nationalId}"
                      placeholder="Enter ID number"
                      @blur="validateField('nationalId')"
                  >
                </div>
                <small v-if="errors.nationalId" class="hr-text-danger">{{ errors.nationalId }}</small>
              </div>

              <!-- Gender -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Gender <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-male-sign"></i></span>
                  <select
                      v-model="employee.gender"
                      class="hr-select"
                      :class="{'is-invalid': errors.gender}"
                      @change="validateField('gender')"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Intersex">Intersex</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <small v-if="errors.gender" class="hr-text-danger">{{ errors.gender }}</small>
              </div>

              <!-- Date of Birth -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Date of Birth <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-calendar"></i></span>
                  <input
                      v-model="employee.dateOfBirth"
                      type="date"
                      class="hr-input"
                      :class="{'is-invalid': errors.dateOfBirth}"
                      :max="maxDate"
                      @blur="validateField('dateOfBirth')"
                  >
                </div>
                <small v-if="errors.dateOfBirth" class="hr-text-danger">{{ errors.dateOfBirth }}</small>
              </div>

              <!-- Nationality -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Nationality <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-flag"></i></span>
                  <select
                      v-model="employee.nationality"
                      class="hr-select"
                      :class="{'is-invalid': errors.nationality}"
                      @change="onNationalityChange"
                  >
                    <option value="">Select Country</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Uganda">Uganda</option>
                    <option value="Tanzania">Tanzania</option>
                    <option value="Rwanda">Rwanda</option>
                    <option value="Ethiopia">Ethiopia</option>
                    <option value="India">India</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <small v-if="errors.nationality" class="hr-text-danger">{{ errors.nationality }}</small>
              </div>

              <!-- Department -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Department <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-building-house"></i></span>
                  <select
                      v-model="employee.departmentID"
                      class="hr-select"
                      :class="{'is-invalid': errors.departmentID}"
                      @change="validateField('departmentID')"
                  >
                    <option value="">Select Department</option>
                    <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                      {{ dept.departmentName }}
                    </option>
                  </select>
                </div>
                <small v-if="errors.departmentID" class="hr-text-danger">{{ errors.departmentID }}</small>
              </div>

              <!-- Position - NEW -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Position <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-briefcase-alt-2"></i></span>
                  <select
                      v-model="employee.positionId"
                      class="hr-select"
                      :class="{'is-invalid': errors.positionId}"
                      @change="handlePositionChange"
                  >
                    <option value="">Select Position</option>
                    <option v-for="pos in positions" :key="pos.id" :value="pos.id">
                      {{ pos.positionName }} ({{ pos.code }})
                      <span v-if="pos.level">- Level {{ pos.level }}</span>
                    </option>
                  </select>
                </div>
                <small v-if="errors.positionId" class="hr-text-danger">{{ errors.positionId }}</small>
              </div>

              <!-- Employment Type -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Employment Type <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-briefcase"></i></span>
                  <select
                      v-model="employee.employmentType"
                      class="hr-select"
                      :class="{'is-invalid': errors.employmentType}"
                      @change="validateField('employmentType')"
                  >
                    <option value="">Select Type</option>
                    <option value="Permanent">Permanent</option>
                    <option value="Contract">Contract</option>
                    <option value="Temporary">Temporary</option>
                    <option value="Intern">Intern</option>
                    <option value="Consultant">Consultant</option>
                  </select>
                </div>
                <small v-if="errors.employmentType" class="hr-text-danger">{{ errors.employmentType }}</small>
              </div>

              <!-- Hire Date -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Hire Date <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-calendar-check"></i></span>
                  <input
                      v-model="employee.hireDate"
                      type="date"
                      class="hr-input"
                      :class="{'is-invalid': errors.hireDate}"
                      @blur="validateField('hireDate')"
                  >
                </div>
                <small v-if="errors.hireDate" class="hr-text-danger">{{ errors.hireDate }}</small>
              </div>

              <!-- Status -->
              <div class="col-md-6 col-lg-4 mb-3">
                <label class="hr-form-label">
                  Status <span class="hr-text-danger">*</span>
                </label>
                <div class="hr-input-group">
                  <span class="hr-input-addon"><i class="bx bx-check-circle"></i></span>
                  <select
                      v-model="employee.status"
                      class="hr-select"
                      :class="{'is-invalid': errors.status}"
                      @change="validateField('status')"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                    <option value="Terminated">Terminated</option>
                  </select>
                </div>
                <small v-if="errors.status" class="hr-text-danger">{{ errors.status }}</small>
              </div>
            </div>

            <!-- Position Details - NEW (shown when position selected) -->
            <div v-if="selectedPosition" class="row mt-3">
              <div class="col-12">
                <div class="bg-light p-3 rounded">
                  <h6 class="mb-3 text-primary">
                    <i class="bx bx-info-circle me-2"></i>
                    Position Details
                  </h6>
                  <div class="row">
                    <div class="col-md-3">
                      <small class="text-muted d-block">Position Code</small>
                      <strong>{{ selectedPosition.code }}</strong>
                    </div>
                    <div class="col-md-3">
                      <small class="text-muted d-block">Level</small>
                      <strong>{{ selectedPosition.level || 'N/A' }}</strong>
                    </div>
                    <div class="col-md-3">
                      <small class="text-muted d-block">Salary Range</small>
                      <strong>
                        {{ formatSalary(selectedPosition.salary_min) }} - 
                        {{ formatSalary(selectedPosition.salary_max) }}
                      </strong>
                    </div>
                    <div class="col-md-3">
                      <small class="text-muted d-block">Supervisor</small>
                      <strong>{{ selectedPosition.reports_to_name || 'N/A' }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Government IDs -->
        <div v-show="currentStep === 2" class="hr-chart-card">
          <div class="hr-chart-header hr-chart-header-primary">
            <h5 class="hr-chart-title hr-mb-0">
              <i class="bx bx-id-card"></i>
              Government Identification Numbers
            </h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-alert hr-alert-info">
              <i class="bx bx-info-circle"></i>
              <strong>Required IDs for {{ employee.nationality || 'selected country' }}:</strong>
              Please provide all mandatory government identification numbers. Fields marked with
              <span class="hr-text-danger">*</span> are required.
            </div>

            <div v-if="filteredGovernmentIdTypes.length === 0" class="text-center py-5">
              <div class="avatar-lg mx-auto mb-4">
                <div class="avatar-title bg-light text-warning rounded-circle">
                  <i class="bx bx-error-circle" style="font-size: 24px;"></i>
                </div>
              </div>
              <h5 class="mt-4">No ID types configured</h5>
              <p class="text-muted">Please select a nationality in the previous step to see required IDs.</p>
            </div>

            <div v-else class="row">
              <div v-for="idType in filteredGovernmentIdTypes" :key="idType.id" class="col-md-6 mb-4">
                <div class="border rounded p-3 h-100" :class="{'border-danger': idType.is_mandatory && !getGovIdValue(idType.id_type_code, 'id_number')}">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <label class="hr-form-label hr-fw-bold hr-mb-1">
                        {{ idType.id_type_name }}
                        <span v-if="idType.is_mandatory" class="hr-text-danger">*</span>
                        <span v-else class="badge bg-secondary ms-1">Optional</span>
                      </label>
                      <p class="text-muted small mb-2">{{ idType.description }}</p>
                      <small v-if="idType.format_example" class="text-muted">
                        <i class="bx bx-info-circle"></i>
                        Format: {{ idType.format_example }}
                      </small>
                    </div>
                    <div class="form-check form-switch">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          :id="`active-${idType.id}`"
                          :checked="getGovIdValue(idType.id_type_code, 'is_active') !== 0"
                          @change="updateGovIdField(idType.id_type_code, 'is_active', $event.target.checked ? 1 : 0)"
                          :disabled="idType.is_mandatory"
                      >
                      <label class="form-check-label small" :for="`active-${idType.id}`">
                        Active
                      </label>
                    </div>
                  </div>

                  <div class="mb-3">
                    <input
                        :value="getGovIdValue(idType.id_type_code, 'id_number')"
                        @input="updateGovIdField(idType.id_type_code, 'id_number', $event.target.value)"
                        type="text"
                        class="hr-input"
                        :class="{'is-invalid': governmentIdErrors[idType.id_type_code]}"
                        :placeholder="`Enter ${idType.id_type_name}`"
                        :maxlength="idType.max_length"
                        @blur="validateGovernmentId(idType)"
                    >
                    <small v-if="governmentIdErrors[idType.id_type_code]" class="hr-text-danger">
                      {{ governmentIdErrors[idType.id_type_code] }}
                    </small>
                  </div>

                  <div class="row">
                    <div class="col-md-6 mb-2">
                      <label class="hr-form-label hr-fs-sm">Issue Date</label>
                      <input
                          :value="getGovIdValue(idType.id_type_code, 'issue_date')"
                          @input="updateGovIdField(idType.id_type_code, 'issue_date', $event.target.value)"
                          type="date"
                          class="hr-input"
                      >
                    </div>
                    <div class="col-md-6 mb-2" v-if="idType.requires_expiry">
                      <label class="hr-form-label hr-fs-sm">Expiry Date <span class="hr-text-danger">*</span></label>
                      <input
                          :value="getGovIdValue(idType.id_type_code, 'expiry_date')"
                          @input="updateGovIdField(idType.id_type_code, 'expiry_date', $event.target.value)"
                          type="date"
                          class="hr-input"
                          :min="today"
                      >
                    </div>
                  </div>

                  <div class="mb-2">
                    <label class="hr-form-label hr-fs-sm">Notes</label>
                    <textarea
                        :value="getGovIdValue(idType.id_type_code, 'notes')"
                        @input="updateGovIdField(idType.id_type_code, 'notes', $event.target.value)"
                        class="hr-input"
                        rows="2"
                        placeholder="Additional notes (optional)"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Documents -->
        <div v-show="currentStep === 3" class="hr-chart-card">
          <div class="hr-chart-header hr-chart-header-primary">
            <h5 class="hr-chart-title hr-mb-0">
              <i class="bx bx-file"></i>
              Employee Documents
            </h5>
          </div>
          <div class="hr-chart-body">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p class="text-muted mb-0">Add and manage employee documents. You can add multiple documents of different types.</p>
              </div>
              <button @click="addDocument" type="button" class="hr-btn hr-btn-primary hr-btn-sm">
                <i class="bx bx-plus-circle"></i>
                Add Document
              </button>
            </div>

            <div v-if="documents.length === 0" class="text-center py-5">
              <div class="avatar-lg mx-auto mb-4">
                <div class="avatar-title bg-light text-primary rounded-circle">
                  <i class="bx bx-file-blank" style="font-size: 24px;"></i>
                </div>
              </div>
              <h5 class="mt-4">No documents added</h5>
              <p class="text-muted">Click "Add Document" to start adding employee documents</p>
            </div>

            <div v-else>
              <div v-for="(doc, index) in documents" :key="index" class="border rounded p-3 mb-3">
                <div class="row">
                  <div class="col-md-3 mb-3">
                    <label class="hr-form-label">Document Type <span class="hr-text-danger">*</span></label>
                    <select v-model="doc.documentType" @change="onDocumentTypeChange(index, $event)" class="hr-select" :class="{'is-invalid': doc.errors?.documentType}">
                      <option value="">Select Type</option>
                      <option v-for="docType in documentTypes" :key="docType.id" :value="docType.type_name">
                        {{ docType.type_name }}
                      </option>
                    </select>
                    <small v-if="doc.errors?.documentType" class="hr-text-danger">{{ doc.errors.documentType }}</small>
                  </div>

                  <div class="col-md-3 mb-3">
                    <label class="hr-form-label">Document Name <span class="hr-text-danger">*</span></label>
                    <input v-model="doc.name" type="text" class="hr-input" :class="{'is-invalid': doc.errors?.name}" placeholder="e.g., University Certificate">
                    <small v-if="doc.errors?.name" class="hr-text-danger">{{ doc.errors.name }}</small>
                  </div>

                  <div class="col-md-3 mb-3">
                    <label class="hr-form-label">Document Number</label>
                    <input v-model="doc.documentNumber" type="text" class="hr-input" placeholder="Certificate/License number">
                  </div>

                  <div class="col-md-3 mb-3">
                    <label class="hr-form-label">Valid Until</label>
                    <input v-model="doc.validTill" type="date" class="hr-input">
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="hr-form-label">Upload Document</label>
                    <input
                        @change="onDocumentFileChange(index, $event)"
                        type="file"
                        class="hr-input"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        :disabled="doc.uploading"
                    >
                    <small class="text-muted">Supported formats: PDF, JPG, PNG, DOC, DOCX (Max: 5MB)</small>

                    <div v-if="doc.uploading" class="mt-2">
                      <div class="d-flex align-items-center">
                        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                        <small class="text-primary">Uploading...</small>
                      </div>
                    </div>

                    <div v-if="doc.filePath && !doc.uploading" class="mt-2">
                      <div class="alert alert-success py-2 mb-0">
                        <i class="mdi mdi-check-circle me-2"></i>
                        <small>{{ doc.originalFileName || doc.name }} uploaded successfully</small>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6 mb-3 d-flex align-items-end">
                    <button @click="removeDocument(index)" type="button" class="hr-btn hr-btn-outline-danger hr-btn-sm">
                      <i class="bx bx-trash"></i>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: Emergency Contacts -->
        <div v-show="currentStep === 4" class="hr-chart-card">
          <div class="hr-chart-header hr-chart-header-primary">
            <h5 class="hr-chart-title hr-mb-0">
              <i class="bx bx-phone-call"></i>
              Emergency Contacts
            </h5>
          </div>
          <div class="hr-chart-body">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p class="text-muted mb-0">Add emergency contact persons who can be reached in case of emergencies.</p>
              </div>
              <button @click="addEmergencyContact" type="button" class="hr-btn hr-btn-primary hr-btn-sm">
                <i class="bx bx-plus-circle"></i>
                Add Contact
              </button>
            </div>

            <div v-if="emergencyContacts.length === 0" class="text-center py-5">
              <div class="avatar-lg mx-auto mb-4">
                <div class="avatar-title bg-light text-primary rounded-circle">
                  <i class="bx bx-phone-plus" style="font-size: 24px;"></i>
                </div>
              </div>
              <h5 class="mt-4">No emergency contacts added</h5>
              <p class="text-muted">Click "Add Contact" to add emergency contact persons</p>
            </div>

            <div v-else>
              <div v-for="(contact, index) in emergencyContacts" :key="index" class="border rounded p-3 mb-3">
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="hr-form-label">Full Name <span class="hr-text-danger">*</span></label>
                    <input v-model="contact.name" type="text" class="hr-input" :class="{'is-invalid': contact.errors?.name}" placeholder="Contact person name">
                    <small v-if="contact.errors?.name" class="hr-text-danger">{{ contact.errors.name }}</small>
                  </div>

                  <div class="col-md-4 mb-3">
                    <label class="hr-form-label">Phone Number <span class="hr-text-danger">*</span></label>
                    <input v-model="contact.phoneNumber" type="tel" class="hr-input" :class="{'is-invalid': contact.errors?.phoneNumber}" placeholder="07XXXXXXXX">
                    <small v-if="contact.errors?.phoneNumber" class="hr-text-danger">{{ contact.errors.phoneNumber }}</small>
                  </div>

                  <div class="col-md-4 mb-3">
                    <label class="hr-form-label">Relationship <span class="hr-text-danger">*</span></label>
                    <select v-model="contact.relationship" class="hr-select" :class="{'is-invalid': contact.errors?.relationship}">
                      <option value="">Select Relationship</option>
                      <option v-for="relType in relationshipTypes" :key="relType.id" :value="relType.relationship_name">
                        {{ relType.relationship_name }}
                      </option>
                    </select>
                    <small v-if="contact.errors?.relationship" class="hr-text-danger">{{ contact.errors.relationship }}</small>
                  </div>

                  <div class="col-md-8 mb-3">
                    <label class="hr-form-label">Email Address</label>
                    <input v-model="contact.email" type="email" class="hr-input" placeholder="contact@example.com">
                  </div>

                  <div class="col-md-4 mb-3 d-flex align-items-end">
                    <button @click="removeEmergencyContact(index)" type="button" class="hr-btn hr-btn-outline-danger hr-btn-sm">
                      <i class="bx bx-trash"></i>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 5: Next of Kin -->
        <div v-show="currentStep === 5" class="hr-chart-card">
          <div class="hr-chart-header hr-chart-header-primary">
            <h5 class="hr-chart-title hr-mb-0">
              <i class="bx bx-heart"></i>
              Next of Kin & Beneficiaries
            </h5>
          </div>
          <div class="hr-chart-body">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p class="text-muted mb-0">Add next of kin and beneficiaries for insurance and benefit purposes.</p>
              </div>
              <button @click="addNextOfKin" type="button" class="hr-btn hr-btn-primary hr-btn-sm">
                <i class="bx bx-plus-circle"></i>
                Add Next of Kin
              </button>
            </div>

            <div v-if="nextOfKin.length === 0" class="text-center py-5">
              <div class="avatar-lg mx-auto mb-4">
                <div class="avatar-title bg-light text-primary rounded-circle">
                  <i class="bx bx-user-plus" style="font-size: 24px;"></i>
                </div>
              </div>
              <h5 class="mt-4">No next of kin added</h5>
              <p class="text-muted">Click "Add Next of Kin" to add beneficiaries</p>
            </div>

            <div v-else>
              <div v-for="(kin, index) in nextOfKin" :key="index" class="border rounded p-3 mb-3">
                <div class="row">
                  <div class="col-md-3 mb-3">
                    <label class="hr-form-label">Full Name <span class="hr-text-danger">*</span></label>
                    <input v-model="kin.name" type="text" class="hr-input" :class="{'is-invalid': kin.errors?.name}" placeholder="Beneficiary name">
                    <small v-if="kin.errors?.name" class="hr-text-danger">{{ kin.errors.name }}</small>
                  </div>

                  <div class="col-md-3 mb-3">
                    <label class="hr-form-label">Phone Number <span class="hr-text-danger">*</span></label>
                    <input v-model="kin.phoneNumber" type="tel" class="hr-input" :class="{'is-invalid': kin.errors?.phoneNumber}" placeholder="07XXXXXXXX">
                    <small v-if="kin.errors?.phoneNumber" class="hr-text-danger">{{ kin.errors.phoneNumber }}</small>
                  </div>

                  <div class="col-md-3 mb-3">
                    <label class="hr-form-label">Relationship <span class="hr-text-danger">*</span></label>
                    <select v-model="kin.relationship" class="hr-select" :class="{'is-invalid': kin.errors?.relationship}">
                      <option value="">Select Relationship</option>
                      <option v-for="relType in relationshipTypes" :key="relType.id" :value="relType.relationship_name">
                        {{ relType.relationship_name }}
                      </option>
                    </select>
                    <small v-if="kin.errors?.relationship" class="hr-text-danger">{{ kin.errors.relationship }}</small>
                  </div>

                  <div class="col-md-3 mb-3">
                    <label class="hr-form-label">Email Address</label>
                    <input v-model="kin.email" type="email" class="hr-input" placeholder="kin@example.com">
                  </div>

                  <div class="col-md-8 mb-3">
                    <div class="form-check">
                      <input v-model="kin.isCoveredInBenefits" class="form-check-input" type="checkbox" :id="`benefits-${index}`">
                      <label class="form-check-label" :for="`benefits-${index}`">
                        Include in benefit coverage (insurance, medical, etc.)
                      </label>
                    </div>
                  </div>

                  <div class="col-md-4 mb-3 d-flex align-items-end">
                    <button @click="removeNextOfKin(index)" type="button" class="hr-btn hr-btn-outline-danger hr-btn-sm">
                      <i class="bx bx-trash"></i>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="hr-chart-card hr-mt-4">
          <div class="hr-chart-body hr-flex hr-justify-between hr-align-center">
            <button v-if="currentStep > 1" @click="previousStep" type="button" class="hr-btn hr-btn-secondary">
              <i class="bx bx-arrow-back"></i> Previous
            </button>
            <div v-else></div>
            <div class="hr-flex hr-gap-2">
              <button v-if="currentStep < 5" @click="nextStep" type="button" class="hr-btn hr-btn-primary" :disabled="!canProceedToNextStep">
                Next <i class="bx bx-arrow-forward"></i>
              </button>
              <button v-if="currentStep === 5" @click="validateAndSubmit" type="button" class="hr-btn hr-btn-success" :disabled="loading">
                <span v-if="!loading"><i class="bx bx-save"></i> Update Employee</span>
                <span v-else><span class="hr-spinner hr-spinner-sm"></span> Updating...</span>
              </button>
            </div>
          </div>
        </div>
        <!-- HR History Timeline (disciplinary & HR actions) -->
        <div v-if="employee && employee.id" class="hr-chart-card hr-mt-4">
          <div class="hr-chart-header hr-chart-header-primary">
            <h5 class="hr-chart-title hr-mb-0"><i class="mdi mdi-gavel"></i> HR history</h5>
          </div>
          <div class="hr-chart-body">
            <EmployeeHrTimeline :employee-id="employee.id" />
          </div>
        </div>
      </div>

      <!-- Success Modal (Custom Vue Modal - no Bootstrap) -->
      <div v-if="showSuccessModal" class="modal-overlay" @click.self="closeSuccessModal">
        <div class="modal-dialog-custom">
          <div class="modal-content-custom">
            <div class="modal-body-custom text-center p-5">
              <div class="mb-4">
                <div class="avatar-lg mx-auto">
                  <div class="avatar-title bg-light text-success rounded-circle">
                    <i class="bx bx-check-circle" style="font-size: 48px;"></i>
                  </div>
                </div>
              </div>
              <h4 class="mb-3">Employee Updated Successfully!</h4>
              <p class="hr-text-muted mb-4">{{ successMessage }}</p>
              <button type="button" @click="gotTo('employee-register')" class="hr-btn hr-btn-primary hr-w-100">
                <i class="bx bx-list-ul"></i> Back to Employee List
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Modal (Custom Vue Modal - no Bootstrap) -->
      <div v-if="showErrorModal" class="modal-overlay" @click.self="closeErrorModal">
        <div class="modal-dialog-custom">
          <div class="modal-content-custom">
            <div class="modal-body-custom text-center p-5">
              <div class="mb-4">
                <div class="avatar-lg mx-auto">
                  <div class="avatar-title bg-light text-danger rounded-circle">
                    <i class="bx bx-error-circle" style="font-size: 48px;"></i>
                  </div>
                </div>
              </div>
              <h4 class="mb-3">Update Failed</h4>
              <p class="hr-text-muted mb-4">{{ errorMessage }}</p>
              <button type="button" @click="closeErrorModal" class="hr-btn hr-btn-danger">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import EmployeeHrTimeline from "@/components/directorHr/EmployeeHrTimeline.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeEdit",
  components: { HrPageLayout, EmployeeHrTimeline },
  data() {
    return {
      currentStep: 1,

      employee: {
        id: '',
        empNo: '',
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        personalEmail: '',
        phone: '',
        nationalId: '',
        gender: '',
        dateOfBirth: '',
        nationality: 'Kenya',
        employmentType: '',
        hireDate: '',
        departmentID: '',
        departmentName: '',
        positionId: '',
        positionTitle: '',
        status: 'Active'
      },

      positions: [],
      selectedPosition: null,

      governmentIds: [],
      governmentIdTypes: [],
      governmentIdErrors: {},

      documents: [],
      emergencyContacts: [],
      nextOfKin: [],

      departments: [],
      documentTypes: [],
      relationshipTypes: [],

      loading: false,
      errors: {},
      successMessage: '',
      errorMessage: '',
      maxDate: '',
      today: '',
      
      // Modal flags (replacing Bootstrap modals)
      showSuccessModal: false,
      showErrorModal: false
    };
  },

  computed: {
    requiresPersonalEmail() {
      return ['Contract', 'Consultant', 'Intern'].includes(this.employee.employmentType || '');
    },

    filteredGovernmentIdTypes() {
      if (!this.employee.nationality) return [];

      const countryMap = {
        'Kenya': 'KE',
        'India': 'IN',
        'United States': 'US',
        'United Kingdom': 'GB'
      };

      const countryCode = countryMap[this.employee.nationality];
      if (!countryCode) return [];

      return this.governmentIdTypes
          .filter(type => type.country_code === countryCode && type.is_active)
          .sort((a, b) => a.display_order - b.display_order);
    },

    canProceedToNextStep() {
      if (this.currentStep === 1) {
        return this.isStep1Valid;
      }
      if (this.currentStep === 2) {
        return this.isStep2Valid;
      }
      return true;
    },

    isStep1Valid() {
      const requiredFields = ['empNo', 'firstName', 'lastName', 'email',
        'phone', 'nationalId', 'gender', 'dateOfBirth', 'nationality',
        'hireDate', 'departmentID', 'employmentType', 'positionId', 'status'];

      const basic = requiredFields.every(field => {
        if (!this.employee[field] || this.employee[field] === '') return false;
        if (field === 'email' && !this.isValidEmail(this.employee.email)) return false;
        if (field === 'phone' && !this.isValidPhone(this.employee.phone)) return false;
        return true;
      });
      if (!basic) return false;
      if (this.requiresPersonalEmail) {
        if (!this.employee.personalEmail || this.employee.personalEmail === '') return false;
        if (!this.isValidEmail(this.employee.personalEmail)) return false;
      }
      return true;
    },

    isStep2Valid() {
      return this.filteredGovernmentIdTypes.every(idType => {
        const govId = this.governmentIds.find(id => id.id_type === idType.id_type_code);

        if (idType.is_mandatory && (!govId || !govId.id_number || govId.id_number.trim() === '')) {
          return false;
        }

        if (govId && govId.id_number) {
          if (idType.min_length && govId.id_number.length < idType.min_length) return false;
          if (idType.max_length && govId.id_number.length > idType.max_length) return false;

          if (idType.format_pattern) {
            try {
              const regex = new RegExp(idType.format_pattern);
              if (!regex.test(govId.id_number)) return false;
            } catch (e) {
              console.error("Invalid regex pattern:", idType.format_pattern);
            }
          }

          if (idType.requires_expiry && (!govId.expiry_date || new Date(govId.expiry_date) < new Date())) {
            return false;
          }
        }

        return true;
      });
    }
  },
  watch: {
  'employee.positionId': {
    handler(newVal) {
      if (newVal && this.positions.length > 0) {
        const pos = this.positions.find(p => p.id == newVal);
        if (pos) {
          this.employee.positionTitle = pos.positionName;
          this.selectedPosition = pos;
        }
      } else {
        this.employee.positionTitle = '';
        this.selectedPosition = null;
      }
    },
    immediate: true
  },
  
  // Watch for positions loading
  positions: {
    handler(newPositions) {
      if (newPositions.length > 0 && this.employee.positionId) {
        const pos = newPositions.find(p => p.id == this.employee.positionId);
        if (pos) {
          this.selectedPosition = pos;
          this.employee.positionTitle = pos.positionName;
        }
      }
    },
    immediate: true
  }
  },

  async mounted() {
    this.setDates();
    await this.loadDepartments();
    await this.loadPositions(); // Wait for positions to load
    await this.loadDropdownData();
    await this.loadGovernmentIdTypes();
    await this.loadEmployee(); // Load employee after positions are loaded
  },

  methods: {
    setDates() {
      const today = new Date();
      this.today = today.toISOString().split('T')[0];

      const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
      this.maxDate = eighteenYearsAgo.toISOString().split('T')[0];
    },

    // Modal methods
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.gotTo('employee-register');
    },

    closeErrorModal() {
      this.showErrorModal = false;
    },

    async loadPositions() {
      try {
        const data = new FormData();
        data.append("function", "getAllPositions");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.positions = res.data.data?.positions || res.data.positions || [];
        }
      } catch (error) {
        console.error("Error loading positions:", error);
      }
    },

    async loadDepartments() {
      try {
        const data = new FormData();
        data.append("function", "getDepartments");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.departments = res.data.data?.departments || res.data.departments || [];
        }
      } catch (error) {
        console.error("Error loading departments:", error);
      }
    },

    async loadDropdownData() {
      try {
        const docData = new FormData();
        docData.append("function", "getDocumentTypes");
        const docRes = await execute(docData, authUrl);
        if (docRes && docRes.data && docRes.data.success) {
          this.documentTypes = docRes.data.data.documentTypes || [];
        }

        const relData = new FormData();
        relData.append("function", "getRelationshipTypes");
        const relRes = await execute(relData, authUrl);
        if (relRes && relRes.data && relRes.data.success) {
          this.relationshipTypes = relRes.data.data.relationshipTypes || [];
        }
      } catch (error) {
        console.error("Error loading dropdown data:", error);
      }
    },

    async loadGovernmentIdTypes() {
      try {
        const data = new FormData();
        data.append("function", "getGovernmentIdTypes");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.governmentIdTypes = res.data.data.governmentIdTypes || [];
        }
      } catch (error) {
        console.error("Error loading government ID types:", error);
      }
    },

    async loadEmployee() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeWithDetails");
        data.append("employeeId", this.$route.params.id || sessionStorage.getItem("employeeId"));

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const empData = res.data.data;

          this.employee = {
            id: empData.employee.id,
            empNo: empData.employee.empNo,
            firstName: empData.employee.firstName,
            middleName: empData.employee.middleName || '',
            lastName: empData.employee.lastName,
            email: empData.employee.email,
            personalEmail: empData.employee.personalEmail || '',
            phone: empData.employee.phone,
            mobileNumber: empData.employee.mobileNumber || '',
            nationalId: empData.employee.nationalId,
            gender: empData.employee.gender,
            dateOfBirth: empData.employee.dateOfBirth,
            nationality: empData.employee.nationality || 'Kenya',
            employmentType: empData.employee.employmentType,
            hireDate: empData.employee.hireDate,
            departmentID: empData.employee.departmentID,
            departmentName: empData.employee.departmentName,
            positionId: empData.employee.positionId || '',
            positionTitle: empData.employee.positionTitle || '',
            status: empData.employee.status || 'Active',
            kraPin: empData.employee.kraPin || '',
            nssfNumber: empData.employee.nssfNumber || '',
            shifNumber: empData.employee.shifNumber || '',
            designationCode: empData.employee.designationCode || '',
            payGroup: empData.employee.payGroup || '',
            doca: empData.employee.doca || ''
          };

          // Set selected position after positions are loaded
          if (this.employee.positionId && this.positions.length > 0) {
            this.selectedPosition = this.positions.find(p => p.id == this.employee.positionId);
          }

          this.governmentIds = empData.governmentIds || [];
          this.documents = empData.documents ? empData.documents.map(doc => ({...doc, errors: {}, uploading: false})) : [];
          this.emergencyContacts = empData.emergencyContacts ? empData.emergencyContacts.map(contact => ({...contact, errors: {}})) : [];
          this.nextOfKin = empData.nextOfKin ? empData.nextOfKin.map(kin => ({
            ...kin,
            isCoveredInBenefits: kin.isCoveredInBenefits === '1' || kin.isCoveredInBenefits === 1,
            errors: {}
          })) : [];
          
          console.log("Loaded employee with position:", {
            positionId: this.employee.positionId,
            positionTitle: this.employee.positionTitle,
            selectedPosition: this.selectedPosition
          });
        } else {
          throw new Error(res.data?.message || 'Failed to load employee');
        }
      } catch (error) {
        this.errorMessage = error.message;
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },

    handlePositionChange() {
      this.validateField('positionId');
      
      if (this.employee.positionId) {
        this.selectedPosition = this.positions.find(p => p.id == this.employee.positionId);
        if (this.selectedPosition) {
          this.employee.positionTitle = this.selectedPosition.positionName;
          
          // Auto-fill department if position has department and no department selected
          if (this.selectedPosition.department_id && !this.employee.departmentID) {
            this.employee.departmentID = this.selectedPosition.department_id;
          }
        }
      } else {
        this.selectedPosition = null;
        this.employee.positionTitle = '';
      }
      
      // Force reactivity
      this.employee = { ...this.employee };
    },

    formatSalary(salary) {
      if (!salary || salary === 0 || salary === '0') return 'N/A';
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'KES',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(salary).replace('KES', 'KES ');
    },

    getGovIdValue(idTypeCode, field) {
      const govId = this.governmentIds.find(id => id.id_type === idTypeCode);
      if (govId) {
        return govId[field] !== undefined && govId[field] !== null ? govId[field] : '';
      }
      const defaults = {
        id_number: '',
        is_active: 1,
        issue_date: '',
        expiry_date: '',
        notes: ''
      };
      return defaults[field];
    },

    updateGovIdField(idTypeCode, field, value) {
      let govId = this.governmentIds.find(id => id.id_type === idTypeCode);

      if (!govId) {
        govId = {
          id_type: idTypeCode,
          id_number: '',
          country_code: this.getCountryCode(this.employee.nationality),
          issue_date: '',
          expiry_date: '',
          is_verified: 0,
          is_active: 1,
          notes: ''
        };
        this.governmentIds.push(govId);
      }

      govId[field] = value;
      
      // Force reactivity
      this.governmentIds = [...this.governmentIds];
    },

    getCountryCode(nationality) {
      const countryMap = {
        'Kenya': 'KE',
        'India': 'IN',
        'United States': 'US',
        'United Kingdom': 'GB'
      };
      return countryMap[nationality] || 'KE';
    },

    onNationalityChange() {
      this.validateField('nationality');
      this.governmentIds = [];
      this.governmentIdErrors = {};
    },

    validateGovernmentId(idType) {
      const govId = this.governmentIds.find(id => id.id_type === idType.id_type_code);

      // Clear previous error
      const newErrors = {...this.governmentIdErrors};
      delete newErrors[idType.id_type_code];
      this.governmentIdErrors = newErrors;

      if (idType.is_mandatory && (!govId || !govId.id_number || govId.id_number.trim() === '')) {
        this.governmentIdErrors = {
          ...this.governmentIdErrors,
          [idType.id_type_code]: `${idType.id_type_name} is required`
        };
        return false;
      }

      if (govId && govId.id_number && govId.id_number.trim() !== '') {
        if (idType.min_length && govId.id_number.length < idType.min_length) {
          this.governmentIdErrors = {
            ...this.governmentIdErrors,
            [idType.id_type_code]: `Minimum ${idType.min_length} characters required`
          };
          return false;
        }

        if (idType.max_length && govId.id_number.length > idType.max_length) {
          this.governmentIdErrors = {
            ...this.governmentIdErrors,
            [idType.id_type_code]: `Maximum ${idType.max_length} characters allowed`
          };
          return false;
        }

        if (idType.format_pattern) {
          try {
            const regex = new RegExp(idType.format_pattern);
            if (!regex.test(govId.id_number)) {
              this.governmentIdErrors = {
                ...this.governmentIdErrors,
                [idType.id_type_code]: `Invalid format. Expected: ${idType.format_example || 'valid format'}`
              };
              return false;
            }
          } catch (e) {
            console.error("Invalid regex pattern");
          }
        }

        if (idType.requires_expiry && (!govId.expiry_date || new Date(govId.expiry_date) < new Date())) {
          this.governmentIdErrors = {
            ...this.governmentIdErrors,
            [idType.id_type_code]: 'Valid expiry date is required'
          };
          return false;
        }
      }

      return true;
    },

    validateAllGovernmentIds() {
      let isValid = true;
      this.governmentIdErrors = {};

      this.filteredGovernmentIdTypes.forEach(idType => {
        if (!this.validateGovernmentId(idType)) {
          isValid = false;
        }
      });

      return isValid;
    },

    nextStep() {
      if (this.currentStep === 1) {
        this.validateStep1();
      } else if (this.currentStep === 2) {
        this.validateAllGovernmentIds();
      }

      if (this.canProceedToNextStep) {
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    validateStep1() {
      const requiredFields = ['empNo', 'firstName', 'lastName', 'email',
        'phone', 'nationalId', 'gender', 'dateOfBirth', 'nationality',
        'hireDate', 'departmentID', 'employmentType', 'positionId', 'status'];

      let isValid = true;
      requiredFields.forEach(field => {
        this.validateField(field);
        if (this.errors[field]) {
          isValid = false;
        }
      });

      return isValid;
    },

    addDocument() {
      this.documents.push({
        documentType: '',
        name: '',
        documentNumber: '',
        filePath: '',
        fileSize: '',
        fileType: '',
        uploadDate: new Date().toISOString().split('T')[0],
        validTill: '',
        uploading: false,
        errors: {}
      });
    },

    removeDocument(index) {
      this.documents.splice(index, 1);
    },

    onDocumentTypeChange(index, event) {
      const selectedType = event.target.value;
      if (selectedType) {
        this.documents[index].name = selectedType;
        this.documents[index].documentType = selectedType;
      } else {
        this.documents[index].name = '';
        this.documents[index].documentType = '';
      }
    },

    async onDocumentFileChange(index, event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        this.$toast?.error("File size must be less than 5MB") || alert('File size must be less than 5MB');
        event.target.value = '';
        return;
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        this.$toast?.error("Only PDF, JPG, PNG, DOC, and DOCX files are allowed") ||
        alert('Only PDF, JPG, PNG, DOC, and DOCX files are allowed');
        event.target.value = '';
        return;
      }

      try {
        this.documents[index].uploading = true;

        const formData = new FormData();
        formData.append('function', 'uploadDocument');
        formData.append('file', file);
        formData.append('documentType', this.documents[index].documentType || 'general');
        formData.append('employeeId', this.employee.id || '');

        const response = await execute(formData, authUrl);

        if (response && response.data && response.data.success) {
          this.documents[index].filePath = response.data.data.filePath;
          this.documents[index].fileSize = response.data.data.fileSize;
          this.documents[index].fileType = response.data.data.fileType;
          this.documents[index].uploadDate = response.data.data.uploadDate;
          this.documents[index].originalFileName = response.data.data.originalName;

          this.$toast?.success("Document uploaded successfully") ||
          console.log('Document uploaded successfully');
        } else {
          throw new Error(response.data?.message || 'Upload failed');
        }
      } catch (error) {
        console.error('Upload error:', error);
        this.$toast?.error("Failed to upload document: " + error.message) ||
        alert('Failed to upload document: ' + error.message);
        event.target.value = '';

        this.documents[index].filePath = '';
        this.documents[index].fileSize = '';
        this.documents[index].fileType = '';
      } finally {
        this.documents[index].uploading = false;
      }
    },

    addEmergencyContact() {
      this.emergencyContacts.push({
        name: '',
        phoneNumber: '',
        email: '',
        relationship: '',
        errors: {}
      });
    },

    removeEmergencyContact(index) {
      this.emergencyContacts.splice(index, 1);
    },

    addNextOfKin() {
      this.nextOfKin.push({
        name: '',
        phoneNumber: '',
        email: '',
        relationship: '',
        isCoveredInBenefits: false,
        errors: {}
      });
    },

    removeNextOfKin(index) {
      this.nextOfKin.splice(index, 1);
    },

    validateField(field) {
      const newErrors = {...this.errors};
      delete newErrors[field];

      const requiredFields = {
        empNo: 'Employee number',
        firstName: 'First name',
        lastName: 'Last name',
        nationalId: 'National ID',
        gender: 'Gender',
        dateOfBirth: 'Date of birth',
        nationality: 'Nationality',
        hireDate: 'Hire date',
        departmentID: 'Department',
        positionId: 'Position',
        employmentType: 'Employment type',
        status: 'Status'
      };

      if (requiredFields[field] && (!this.employee[field] || this.employee[field] === '')) {
        newErrors[field] = `${requiredFields[field]} is required`;
      }

      if (field === 'email') {
        if (!this.employee.email || this.employee.email === '') {
          newErrors[field] = 'Email is required';
        } else if (!this.isValidEmail(this.employee.email)) {
          newErrors[field] = 'Please enter a valid email address';
        }
      }

      if (field === 'phone') {
        if (!this.employee.phone || this.employee.phone === '') {
          newErrors[field] = 'Phone number is required';
        } else if (!this.isValidPhone(this.employee.phone)) {
          newErrors[field] = 'Please enter a valid phone number';
        }
      }

      if (field === 'personalEmail' && this.requiresPersonalEmail) {
        if (!this.employee.personalEmail || this.employee.personalEmail === '') {
          newErrors[field] = 'Personal email is required for ' + (this.employee.employmentType || '');
        } else if (!this.isValidEmail(this.employee.personalEmail)) {
          newErrors[field] = 'Please enter a valid personal email address';
        }
      }

      this.errors = newErrors;
    },

    validateDynamicArrays() {
      let isValid = true;

      // Documents validation
      this.documents.forEach((doc) => {
        doc.errors = {};
        if (doc.name || doc.filePath) { // Only validate if document exists
          if (!doc.documentType) {
            doc.errors.documentType = 'Document type is required';
            isValid = false;
          }
          if (!doc.name) {
            doc.errors.name = 'Document name is required';
            isValid = false;
          }
        }
      });

      // Emergency Contacts validation (optional - no validation needed)
      // Remove validation for emergency contacts

      // Next of Kin validation
      this.nextOfKin.forEach((kin) => {
        kin.errors = {};
        if (kin.name || kin.phoneNumber) { // Only validate if entry exists
          if (!kin.name) {
            kin.errors.name = 'Name is required';
            isValid = false;
          }
          if (!kin.phoneNumber) {
            kin.errors.phoneNumber = 'Phone number is required';
            isValid = false;
          }
          if (!kin.relationship) {
            kin.errors.relationship = 'Relationship is required';
            isValid = false;
          }
        }
      });

      return isValid;
    },

    validateAllFields() {
      const fields = ['empNo', 'firstName', 'lastName', 'email', 'personalEmail',
        'phone', 'nationalId', 'gender', 'dateOfBirth', 'nationality',
        'hireDate', 'departmentID', 'employmentType', 'positionId', 'status'];

      fields.forEach(field => this.validateField(field));

      const step1Valid = Object.keys(this.errors).length === 0;
      const govIdsValid = this.validateAllGovernmentIds();
      const arraysValid = this.validateDynamicArrays();

      return step1Valid && govIdsValid && arraysValid;
    },

    isValidEmail(email) {
      if (!email) return false;
      const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return re.test(email);
    },

    isValidPhone(phone) {
      if (!phone) return false;
      const re = /^[\d\s\-\+\(\)]+$/;
      return re.test(phone) && phone.replace(/\D/g, '').length >= 9;
    },

    async validateAndSubmit() {
      if (!this.validateAllFields()) {
        this.$toast?.error("Please fill in all required fields correctly") ||
        alert("Please fill in all required fields correctly");
        return;
      }

      await this.updateEmployee();
    },

    async updateEmployee() {
      this.loading = true;

      try {
        const selectedDept = this.departments.find(d => d.id == this.employee.departmentID);
        if (selectedDept) {
          this.employee.departmentName = selectedDept.departmentName;
        }

        // CRITICAL: Ensure position data is properly set
        if (this.selectedPosition) {
          this.employee.positionId = this.selectedPosition.id;
          this.employee.positionTitle = this.selectedPosition.positionName;
        } else {
          this.employee.positionId = '';
          this.employee.positionTitle = '';
        }

        // Log position data for debugging
        console.log("Position being saved:", {
          id: this.employee.positionId,
          title: this.employee.positionTitle,
          selectedPosition: this.selectedPosition
        });

        // Prepare next of kin data
        const preparedNextOfKin = this.nextOfKin
          .filter(kin => kin.name || kin.phoneNumber)
          .map(kin => ({
            name: kin.name || '',
            phoneNumber: kin.phoneNumber || '',
            email: kin.email || '',
            relationship: kin.relationship || '',
            isCoveredInBenefits: kin.isCoveredInBenefits ? '1' : '0'
          }));

        // Prepare emergency contacts data (optional)
        const preparedEmergencyContacts = this.emergencyContacts
          .filter(contact => contact.name || contact.phoneNumber)
          .map(contact => ({
            name: contact.name || '',
            phoneNumber: contact.phoneNumber || '',
            email: contact.email || '',
            relationship: contact.relationship || ''
          }));

        // Prepare documents data
        const preparedDocuments = this.documents
          .filter(doc => doc.name || doc.filePath)
          .map(doc => ({
            documentType: doc.documentType || '',
            name: doc.name || '',
            documentNumber: doc.documentNumber || '',
            filePath: doc.filePath || '',
            fileSize: doc.fileSize || '',
            fileType: doc.fileType || '',
            uploadDate: doc.uploadDate || new Date().toISOString().split('T')[0],
            validTill: doc.validTill || ''
          }));

        // Prepare government IDs
        const activeGovernmentIds = this.governmentIds
          .filter(id => id.is_active && id.id_number && id.id_number.trim() !== '')
          .map(id => ({
            id_type: id.id_type,
            id_number: id.id_number,
            country_code: id.country_code || this.getCountryCode(this.employee.nationality),
            issue_date: id.issue_date || null,
            expiry_date: id.expiry_date || null,
            is_active: id.is_active ? 1 : 0,
            notes: id.notes || ''
          }));

        const data = new FormData();
        data.append("function", "updateEmployeeWithDetails");
        data.append("employeeId", this.employee.id);
        data.append("employee", JSON.stringify(this.employee));
        data.append("governmentIds", JSON.stringify(activeGovernmentIds));
        data.append("documents", JSON.stringify(preparedDocuments));
        data.append("emergencyContacts", JSON.stringify(preparedEmergencyContacts));
        data.append("nextOfKin", JSON.stringify(preparedNextOfKin));

        // Debug logs
        console.log("Complete employee object being sent:", this.employee);
        console.log("Position fields specifically:", {
          positionId: this.employee.positionId,
          positionTitle: this.employee.positionTitle
        });

        const response = await execute(data, authUrl);

        console.log("Update response:", response);

        if (response && response.data) {
          if (response.data.success === false) {
            throw new Error(response.data.message || 'Failed to update employee');
          }
          
          this.successMessage = `Employee ${this.employee.firstName} ${this.employee.lastName} and all details have been updated successfully.`;
          this.showSuccessModal = true;
        } else {
          throw new Error('Failed to update employee');
        }
      } catch (error) {
        console.error('Update error:', error);
        this.errorMessage = error.message || 'An error occurred while updating the employee';
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },

    gotTo(route) {
      if (typeof route === "string" && route.startsWith("/")) {
        this.$router.push(route);
      } else {
        this.$router.push({ name: route });
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.employee-edit-content { padding: 0; }
.employee-edit-form-wrap { max-width: 900px; margin: 0 auto; }
.hr-chart-header-primary { background: linear-gradient(135deg, #059669 0%, #16a34a 100%); color: #fff; }
.hr-chart-header-primary .hr-chart-title { color: #fff; }
.hr-input-group { display: flex; align-items: stretch; }
.hr-input-addon { display: flex; align-items: center; padding: 0 12px; background: #f1f5f9; border: 1px solid #e2e8f0; border-right: none; border-radius: 10px 0 0 10px; color: #64748b; }
.hr-input-group .hr-input { border-radius: 0 10px 10px 0; }
.hr-fw-bold { font-weight: 600; }
.hr-fs-sm { font-size: 12px; }
.hr-mb-1 { margin-bottom: 4px; }
.hr-mt-4 { margin-top: 16px; }
.hr-gap-2 { gap: 12px; }
.hr-w-100 { width: 100%; }
.hr-spinner-sm { width: 18px; height: 18px; border-width: 2px; display: inline-block; vertical-align: middle; margin-right: 8px; }
.badge.bg-secondary { background: #94a3b8; color: #fff; padding: 2px 8px; border-radius: 6px; font-size: 11px; }
.hr-btn-outline-danger { background: transparent; color: #ef4444; border: 2px solid #ef4444; }
.hr-btn-outline-danger:hover { background: rgba(239,68,68,0.1); }

/* Custom Modal Styles (replacing Bootstrap) */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

.modal-dialog-custom {
  max-width: 500px;
  width: 90%;
  animation: slideUp 0.3s ease;
}

.modal-content-custom {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.modal-body-custom {
  padding: 24px;
}

.avatar-lg {
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.progress-nav {
  padding: 1rem 0;
}

.progress-steps {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.progress-steps::before {
  content: '';
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  height: 2px;
  background: #e9ecef;
  z-index: 1;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  background: white;
  padding: 0 1rem;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.step-title {
  font-size: 0.875rem;
  color: #6c757d;
  text-align: center;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: #059669;
  color: white;
}

.step.active .step-title {
  color: #059669;
  font-weight: 600;
}

.step.completed .step-number {
  background: #34c38f;
  color: white;
}

.step.completed .step-title {
  color: #34c38f;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #059669;
  box-shadow: 0 0 0 0.2rem rgba(91, 115, 232, 0.25);
}

.is-invalid {
  border-color: #f46a6a !important;
}

.card {
  transition: box-shadow 0.3s ease;
  margin-bottom: 1.5rem;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
}

.input-group .form-control,
.input-group .form-select {
  border-left: none;
}

.input-group:focus-within .input-group-text {
  border-color: #059669;
  color: #059669;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.border {
  border-color: #e9ecef !important;
}

.border:hover {
  border-color: #059669 !important;
  transition: border-color 0.3s ease;
}

.border-danger {
  border-color: #f46a6a !important;
  border-width: 2px !important;
}

@media (max-width: 768px) {
  .progress-steps {
    flex-wrap: wrap;
    gap: 1rem;
  }

  .step {
    flex: 1;
    min-width: calc(50% - 0.5rem);
  }

  .progress-steps::before {
    display: none;
  }

  .step-title {
    font-size: 0.75rem;
  }
}
</style>